import { BasicPDEXInterface, SegmentationInput as SegmentationInputType } from 'api/product/types';

export type SegmentationInput = Omit<
  SegmentationInputType,
  'food_types' | 'included_tags' | 'sort_by_tags'
>;

export interface PersonalProductDetailsInput extends BasicPDEXInterface {
  product_id: string;
  coupon_code?: string | null;
  rule_id?: string;
  segmentation?: SegmentationInput;
  last_visited_collection?: string | null;
}

export type ProductPriceObj = Omit<
  PersonalizedProductDetailsResponse,
  'id' | 'bestseller' | 'recommendation_text' | 'usp_title' | 'usp'
>;

const TEST_KEY: keyof ProductPriceObj = 'variants';

export function isProductPriceObj(arg: any): arg is ProductPriceObj {
  return typeof arg === 'object' && TEST_KEY in arg;
}

export interface PersonalizedProductDetailsApiResponse {
  dynamics: {
    /** Definition of applied coupon  */
    coupon: CouponDefinition | null;
    variants: PersonalDiscountedVariant[];
    id: string;
    /** True if the product is bestseller for certain set of segmentation  */
    bestseller: boolean;
    /** Recommendation text displayed on PDP on the image */
    recommendation_text: string;
    /** Title displayed on usp  */
    usp_title: string;
    /** Usp on pdp. Currently it's not implemented yet */
    usp: unknown;
  };
}

export type PersonalizedProductDetailsResponse = PersonalizedProductDetailsApiResponse['dynamics'];

export interface CouponSettingResponse {
  /** Comments for internal use  */
  campaignName: string;
  /** Comments for internal use  */
  comments: string;
  /** Valid from date in Unix time */
  validFrom: number;
  /** Valid to date in Unix time */
  validTo: number;
  /** Discount in percentage  */
  percentage: number;
  /** Discount in actual number from cart total   */
  absoluteValue: string;
  /** True if shipping is free  */
  freeShipping: boolean;
  /** Minimum order value for     */
  minimumBasketValue: number;
  /** Type of customer applicable to this coupon, all customer, new or existing */
  customerFidelity?: string;
  include: {
    productIds: Array<number>;
    variantIds: Array<number>;
    tags: Array<string>;
    customerTags: Array<string>;
    productTypes: Array<string>;
  };
  exclude: {
    productIds: Array<number>;
    variantIds: Array<number>;
    tags: Array<string>;
    customerTags: Array<string>;
    productTypes: Array<string>;
  };
  /** The limit to the number of application of the coupon */
  maxRedemptions: number;
  /** The limit to the number of application of the coupon per customer*/
  maxRedemptionsPerCustomer: number;
  /** True if it's applicable to subscription  */
  appliesToSubscriptions: boolean;
  countryCode?: string;
}

export interface CouponDefinition extends CouponSettingResponse {
  code: string;
  appliesToOneOff?: boolean;
}

/**
 * Price object calculated based on comparedAt price
 * Pay attention that percentage is calculated based on comparedAt price for subscription,
 * meaning that discount percentage is bigger than subscription discount.
 * Use ``discounts[index].value`` to show sub discount percentage.
 * 
 * @example
 * // when 20% discount is applied to comparedAt price. 
 * // Percentage is bigger than 20 because it's calculated with comparedAt price
 * {
    "after": "63.99",
    "before": "99.98",
    "diff": "35.99",
    "pct": "36.0"
}
 * */
interface Compare {
  /** Price after discount  */
  after: string;
  /** Price difference after discount  */
  diff: string;
  /** Price difference in percentage  */
  pct: string;
  /** Original price  */
  before: string;
}

interface BasePriceVariant {
  /** Compared at price. Null if no discount is applied  */
  compare: Compare | null;
  /** Price */
  price: string;
  discounts: [
    {
      /** Shows what discount is applied/ */
      origin: 'coupon' | 'contentful' | 'subscription' | 'savings_plan';
      /** Unit of discount value. Only percentage for our use cases */
      unit: 'pct';
      /** Value in associated percentage e.g 5 => 5% */
      value: string;
      /** Amount of discount for variant e.g 0.3 => 30 cent discount */
      amount: string;
    },
  ];
}

interface SubscriptionVariant extends BasePriceVariant {
  /** Compared at price. For subscription it's always available   */
  compare: Compare;
  /** Forecast for subscription price in future */
  forecast: {
    /** Price of subscription  */
    price: string;
    /** Amount of discount in percentage e.g 10 => 10% */
    discount: string;
  };
}

export interface PersonalDiscountedVariant {
  /** Variant ID */
  id: string;
  /** One off purchase price variant */
  one_off: BasePriceVariant;
  /** Subscription purchase price variant */
  subscription?: SubscriptionVariant;
}

const TEST_KEY_ONE_OFF: keyof PersonalDiscountedVariant = 'one_off';
const TEST_KEY_ID: keyof PersonalDiscountedVariant = 'id';
const TEST_KEY_SUBSCRIPTION: keyof PersonalDiscountedVariant = 'subscription';
const TEST_KEY_PRICE: keyof BasePriceVariant = 'price';
const TEST_KEY_DISCOUNTS: keyof BasePriceVariant = 'discounts';
const TEST_KEY_COMPARE: keyof BasePriceVariant = 'compare';

export function isPersonalDiscountedVariant(arg: any): arg is PersonalDiscountedVariant {
  return (
    typeof arg === 'object' &&
    arg !== null &&
    TEST_KEY_ID in arg &&
    typeof arg[TEST_KEY_ID] === 'string' &&
    TEST_KEY_ONE_OFF in arg &&
    typeof arg[TEST_KEY_ONE_OFF] === 'object' &&
    TEST_KEY_PRICE in arg[TEST_KEY_ONE_OFF] &&
    typeof arg[TEST_KEY_ONE_OFF][TEST_KEY_PRICE] === 'string' &&
    TEST_KEY_DISCOUNTS in arg[TEST_KEY_ONE_OFF] &&
    Array.isArray(arg[TEST_KEY_ONE_OFF][TEST_KEY_DISCOUNTS]) &&
    (!arg[TEST_KEY_SUBSCRIPTION] ||
      (typeof arg[TEST_KEY_SUBSCRIPTION] === 'object' &&
        TEST_KEY_PRICE in arg[TEST_KEY_SUBSCRIPTION] &&
        typeof arg[TEST_KEY_SUBSCRIPTION][TEST_KEY_PRICE] === 'string' &&
        TEST_KEY_COMPARE in arg[TEST_KEY_SUBSCRIPTION] &&
        typeof arg[TEST_KEY_SUBSCRIPTION][TEST_KEY_COMPARE] === 'object' &&
        'forecast' in arg[TEST_KEY_SUBSCRIPTION] &&
        typeof arg[TEST_KEY_SUBSCRIPTION].forecast === 'object'))
  );
}
